import { BCMSRecord, DisabledPrompt, useWorkflowStep } from "@bcm-dev/bcms-vite";
import { getDefaultDiscovery, getWorkflowSteps } from "compid/workflows/BillDiscoveryWorkflow";
import BillDiscoveryItem from "compid/workflows/BillDiscoveryWorkflow/classes/BillDiscoveryItem";
import { Biller } from "model";
import React, { useState } from "react";

import ButtonContainer from "compid/shared/containers/ButtonContainer";
import PageLayoutContainer from "compid/shared/containers/PageLayoutContainer";
import CenterColumnContainer from "compid/shared/containers/PageLayoutContainer/containers/CenterColumnContainer";
import PageView from 'compid/shared/containers/PageViewContainer';
import UIButton from "compid/ui/UIButton";
import UICheckbox from "compid/ui/UICheckbox";
import { ImageAsset, SVGAsset } from "res";

import "./styles.css";

/**
 * @function SearchResultsPage
 * @param {STRING} billerName - Set the name of the biller. 
 * @param {STRING} billerNumber - Set the number of the biller. 
 * @param {STRING} billerLogo - Set the logo of the biller. 
 * @return {React.Component}
 * @exported true
 * @description The main component for SearchResultsPage
 * @example
 * <SearchResultsPage />
 */
export default function SearchResultsPage(props) {
  const workflowStep = useWorkflowStep(getDefaultDiscovery, getWorkflowSteps);
  const { quickAdd, verificationAdd, addedItems } = workflowStep.getStepState;

  const toggleItem = discoveryItem => {
    const updated = addedItems.find(item => item.biller.id === discoveryItem.biller.id) !== undefined
      ? addedItems.filter(item => item.biller.id !== discoveryItem.biller.id, [])
      : [ ...addedItems, discoveryItem ];

    workflowStep.update([ "addedItems" ], updated);
  };
  const updateItem = discoveryItem => {
    const updated = addedItems.map(item => item.biller.id === discoveryItem.biller.id ? discoveryItem : item, []);
    workflowStep.update([ "addedItems" ], updated);
  };

  const quickAddItems = quickAdd.map(item => {
    return <BillerCheckboxRow key={ `Biller_${ item.biller.id }` } item={ item } onChange={ toggleItem } />
  }, []);
  const verificationAddItems = verificationAdd.map(item => {
    return <BillerVerificationCheckboxRow key={ `Biller_${ item.biller.id }` } item={ item } onChange={ toggleItem } onUpdate={ updateItem } />
  }, []);

  const cancel = evt => workflowStep.navigate("/paybills");
  const submit = evt => {
    const updates = addedItems.map(item => Biller.update({ ...item.biller.record, "billerAddType" : "" }), []);
    BCMSRecord.batch(updates, workflowStep.globalController);

    workflowStep.navigate("/PayBills", { state: { "addedBillers" : addedItems.map(item => item.biller.record, []) } });
  };

  const hasUnverified = addedItems.reduce((result, item) => item.biller.addType === "verification_add" && item.verification.length === 0 ? true : result, false);
  const hasItems = addedItems.length > 0;

  const disabled = hasItems && !hasUnverified ? "" : "disabled-button";

  return (
    <PageView className="search-results-page">
      <p className="page-sub-header semi-bold-text">SEARCH RESULTS</p>
      <PageLayoutContainer>
        <CenterColumnContainer>
          <SVGAsset id="UpsellIcon" className="info-icon center-align" />
          <div className="form-group">
            <p className="text-center"><b>Pick the ones you want to pay. We'll add the account info for you.</b></p>

            { quickAddItems.length > 0 && <p className="quick-add"><b>Quick add</b></p> }
            { quickAddItems }

            { verificationAddItems.length > 0 && <p className="verification-add"><b>Add With Verification</b></p> }
            { verificationAddItems }

            <ButtonContainer className="form-button-container">
              <UIButton className="secondary-button" onClick={ cancel }>Cancel</UIButton>
              <UIButton className={ `primary-button ${ disabled }` } onClick={ submit } dataPoint="Search_Results_Add_Bills">Add bills</UIButton>
            </ButtonContainer>

            <DisabledPrompt>
              <p className="text-center automatic-search">
                <a>
                  <u>
                    Turn off automatic search
                  </u>
                </a>
              </p>
            </DisabledPrompt>
          </div>
        </CenterColumnContainer>
      </PageLayoutContainer>
    </PageView>
  );
}

/**
 * @function BillerCheckboxRow
 * @param {STRING} billerName - Set the name of the biller. 
 * @param {STRING} billerNumber - Set the number of the biller. 
 * @param {STRING} billerLogo - Set the logo of the biller. 
 * @return {React.Component}
 * @exported false
 * @description This is the checkbox row that shows each available bill for the quick add bills. 
 * @example
 * <BillerCheckboxRow billerName="Pizza Place" billerNumber="x1234" billerLogo="PizzaPlaceLogo" />
 */
function BillerCheckboxRow(props) {
  const item = props.item ?? BillDiscoveryItem.init(Biller.firstInstanceOf());
  const onChange = props.onChange ?? undefined;

  return (
    <div className="biller-checkbox-row form-row row-container"> 
      <div className="flex-container flex-horizontal align-center">
        <div className="set-width">
          <UICheckbox onChange={ () => onChange(item) } dataPoint={ `Biller_Row_Checkbox_${ item.biller.id }` } />
        </div>
        <ImageAsset id={ item.biller.logo } className="left-icon logo-icon set-width" data-point={ `Biller_Row_Logo_${ item.biller.id }` } />
        <p className="flex-width flex-container flex-horizontal align-center flex-wrap">
          <b data-point={ `Biller_Row_Name_${ item.biller.id }` }>{ item.biller.name }</b> 
          <span className="gray-text sub-text" data-point={ `Biller_Row_Number_${ item.biller.id }` }>{ item.biller.number }</span>
        </p>

        <DisabledPrompt>
          <a className="remove-link">Remove</a>
        </DisabledPrompt>
      </div>
    </div>
  );
}

function BillerVerificationCheckboxRow(props) {
  const item = props.item ?? BillDiscoveryItem.init(Biller.firstInstanceOf());
  const ref = React.createRef();
  
  const [ state, setState ] = useState(false);

  const onCheckboxChange = value => {
    setState(value);

    if(props.onChange instanceof Function) {
      props.onChange(item);
    }
  };
  const onInputChange = evt => {
    if(props.onUpdate instanceof Function) {
      item.verification = ref.current.value;
      props.onUpdate(item);
    }
  }

  return (
    <div className="biller-checkbox-row form-row row-container"> 
      <div className="flex-container flex-horizontal align-center">
        <div className="set-width">
          <UICheckbox onChange={ onCheckboxChange } dataPoint={ `Biller_Row_Checkbox_${ item.biller.id }` } />
        </div>
        <ImageAsset id={ item.biller.logo } className="left-icon logo-icon set-width" data-point={ `Biller_Row_Logo_${ item.biller.id }` } />
        <p className="flex-width">
          <b data-point={ `Biller_Row_Name_${ item.biller.id }` }>{ item.biller.name }</b> 
          {/*<span className="gray-text sub-text">{ item.biller.number }</span>*/}
        </p>

        <DisabledPrompt>
          <a className="remove-link">Remove</a>
        </DisabledPrompt>
      </div>

      { state && 
        <div className="flex-container flex-horizontal verification-row">
          <input className="verification-field" placeholder="Enter the last 4 digits of your account number" ref={ ref } onChange={ onInputChange } data-point={ `Biller_Row_Verification_${ item.biller.id }` } />
        </div> }
    </div>
  );
}